import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { confirmAlert } from "react-confirm-alert";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { successDefault } from "../../../util/ToastUtil";
import { page, remove, uploadImage } from "./api-comunicado-banner";
import ComunicadoBannerForm from "./ComunicadoBannerForm";
import ComunicadoBannerList from "./ComunicadoBannerList";
import Container from "../../UI/Container";

const EMPTY_PAGE = { content: [], size: 0, number: 0, totalElements: 0 };

const ComunicadoBannerContainer = () => {
  const [comunicadoBanner, setComunicadoBanner] = useState(null);
  const [listCB, setListCB] = useState(EMPTY_PAGE);
  const [pageNumber, setPageNumber] = useState(0);
  // Incrementado para forçar o recarregamento da página atual (após salvar/remover).
  const [reloadKey, setReloadKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAction, setIsLoadingAction] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    page(pageNumber)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setListCB(data);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [pageNumber, reloadKey]);

  const reloadFromStart = useCallback(() => {
    setPageNumber(0);
    setReloadKey((key) => key + 1);
  }, []);

  const handleSubmit = useCallback(
    async (formData) => {
      setIsLoadingAction(true);
      try {
        const data = await uploadImage(formData);
        if (!data) return;
        if (data.error) {
          errorMessage(data.message);
          return;
        }
        setComunicadoBanner(null);
        reloadFromStart();
        successDefault();
      } catch (err) {
        console.error(err);
        errorMessage("Não foi possível salvar o banner. Tente novamente.");
      } finally {
        setIsLoadingAction(false);
      }
    },
    [reloadFromStart],
  );

  const removeBanner = useCallback(
    async (codigo) => {
      setIsLoadingAction(true);
      try {
        const data = await remove(codigo);
        if (data?.error) {
          errorMessage(data.message);
          return;
        }
        setComunicadoBanner(null);
        // Se o item removido era o único da página, volta uma página.
        if (listCB.content.length === 1 && pageNumber > 0) {
          setPageNumber((p) => p - 1);
        } else {
          setReloadKey((key) => key + 1);
        }
        successDefault();
      } catch (err) {
        console.error(err);
        errorMessage("Não foi possível remover o banner. Tente novamente.");
      } finally {
        setIsLoadingAction(false);
      }
    },
    [listCB.content.length, pageNumber],
  );

  const handleRemove = () => {
    const { codigo } = comunicadoBanner;
    confirmAlert({
      title: "Confirmação",
      message: "Deseja remover este comunicado banner?",
      buttons: [
        { label: "Sim", onClick: () => removeBanner(codigo) },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  return (
    <Container>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h6">Comunicado Banner</Typography>
          <Typography variant="body2" color="text.secondary">
            Imagens que podem ser usadas como banner nos comunicados.
          </Typography>
        </Box>
        {!comunicadoBanner && (
          <Button
            variant="contained"
            startIcon={<AddPhotoAlternateOutlinedIcon />}
            onClick={() => setComunicadoBanner({ eNovo: true })}
          >
            Novo Banner
          </Button>
        )}
      </Stack>

      {comunicadoBanner ? (
        <ComunicadoBannerForm
          comunicadoBanner={comunicadoBanner}
          setComunicadoBanner={setComunicadoBanner}
          handleSubmit={handleSubmit}
          isLoadingAction={isLoadingAction}
          handleRemove={handleRemove}
        />
      ) : (
        <ComunicadoBannerList
          listCB={listCB}
          isLoading={isLoading}
          setComunicadoBanner={setComunicadoBanner}
          setPageNumber={setPageNumber}
        />
      )}
    </Container>
  );
};

export default ComunicadoBannerContainer;
